import React, { useEffect, useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";

import Toggle from "@cayuse/shared-react.components.form.toggle";

import Table from "components/Table";
import { useDictionary } from "hooks";
import {
	editRoleById,
	findRoleInArray,
	getId,
	moveRole,
} from "store/actions/researchRoles";

import { researchRolesSelectors } from "../../store/selectors";
import DragHandle from "../Form/SimplifiedBuilder/Internal/DragHandle";
import { EditRoleForm } from "./EditRoleForm";
import styles from "./styles.css";

const DRAG_ITEM_TYPES = {
	ROLE_ITEM: "role",
	DISABLED_ITEM: "disabled",
};

export const RolesTableRow = props => {
	const { role, isEditMode, actionsDisabled, index, numberOfRoles } = props;

	const dictionary = useDictionary("manageResearchTeamRoles");

	const id = getId(role);

	const canDragNDrop = !role.isPrincipalInvestigatorRole;

	const rowRef = useRef(null);
	const dragHandleRef = useRef(null);
	const [ toIndex, setToIndex ] = useState(index);
	const [ isDragging, setIsDragging ] = useState(false);
	const [ initIndex, setInitIndex ] = useState(index);
	const maxIndex = researchRolesSelectors.content().changed.length;
	const [ { isDragging: isMouseDragging }, drag ] = useDrag({
		item: {
			id,
			index,
			type: canDragNDrop ? DRAG_ITEM_TYPES.ROLE_ITEM : DRAG_ITEM_TYPES.DISABLED_ITEM,
		},
		canDrag: canDragNDrop,
		collect: monitor => ({
			isDragging: monitor.isDragging(),
		}),
		end: (dropResult, monitor) => {
			const { id: droppedId, index } = monitor.getItem();
			const didDrop = monitor.didDrop();
			if (!didDrop) {
				moveRole(droppedId, index);
			}
			setIsDragging(false);
			setToIndex(index);
		},
	});

	const [ , drop ] = useDrop({
		accept: canDragNDrop ? DRAG_ITEM_TYPES.ROLE_ITEM : DRAG_ITEM_TYPES.DISABLED_ITEM,
		hover: ({ id: draggedId }) => {
			if (draggedId !== id) {
				const { index: overIndex } = findRoleInArray(id);
				moveRole(draggedId, overIndex);
				setToIndex(overIndex);
			}
		},
	});

	useEffect(() => {
		if (!isEditMode) {
			setIsDragging(false);
			setToIndex(index);
		}
	}, [ isEditMode, index ]);

	drag(dragHandleRef);
	drop(rowRef);

	const liveRegionContentTimeout = useRef(null);

	const announceLiveRegion = text => {
		const element = document.getElementById("roles-table-live-region");
		element.textContent = text;

		if (liveRegionContentTimeout.current) {
			clearTimeout(liveRegionContentTimeout.current);
		}
		liveRegionContentTimeout.current = setTimeout(() => {
			element.textContent = "";
		}, 7_000);
	};

	const activateDraggingMode = e => {
		e.preventDefault();
		if (index !== 0) {
			if (isDragging) {
				setIsDragging(false);
				announceLiveRegion(`Item ${role.name} dropped.`);
			} else {
				setInitIndex(index);
				setToIndex(index);
				setIsDragging(true);
				announceLiveRegion(`Item ${role.name} grabbed. ${role.name} is now item #${index + 1} of ${numberOfRoles}` );
			}
		}
	};

	const handleKeyDown = e => {
		if (!isEditMode || actionsDisabled) {
			return;
		}
		switch (e.key) {
		case " ":
			activateDraggingMode(e);
			break;
		case "Enter":
			activateDraggingMode(e);
			break;
		case "ArrowUp":
			e.preventDefault();
			if (isDragging && toIndex > 1) {
				const newIndex = toIndex - 1;
				moveRole(id, newIndex);
				setToIndex(newIndex);
				announceLiveRegion(`${role.name} is now item #${newIndex + 1} of ${numberOfRoles}`);
			}
			break;
		case "ArrowDown":
			e.preventDefault();
			if (isDragging) {
				const newIndex = toIndex + 1;
				if (newIndex < maxIndex) {
					moveRole(id, newIndex);
					setToIndex(newIndex);
					announceLiveRegion(`${role.name} is now item #${newIndex + 1} of ${numberOfRoles}`);
				}
			}
			break;
		case "Escape":
			e.preventDefault();
			if (isDragging) {
				setIsDragging(false);
				moveRole(id, initIndex);
				setToIndex(initIndex);
				announceLiveRegion("Reorder cancelled.");
			}
			break;
		case "Tab":
			if (isDragging) {
				setIsDragging(false);
				announceLiveRegion("Reorder cancelled.");
			}
			break;
		default:
			return;
		}
	};

	const isBeingDragged = isMouseDragging || isDragging;

	return (
		<tr style={{ opacity: isBeingDragged ? 0.5 : 1 }} ref={rowRef} className={styles.tableRow}>
			<Table.Cell style={{ display: !isEditMode ? "none" : "table-cell" }}>
				{canDragNDrop ? (
					<div role="application" aria-roledescription="drag button">
						<button
							className={styles.tableRowDragButton}
							aria-describedby="roles-table-reorder-help"
							draggable="true"
							ref={dragHandleRef}
							onKeyDown={handleKeyDown}
							aria-label={`Reorder ${role.name}.`}
						>
							<DragHandle />
						</button>
					</div>
				) : null}
			</Table.Cell>
			{isEditMode ? (
				<EditRoleForm
					role={role}
					actionsDisabled={actionsDisabled}
				/>) : (
				<>
					<Table.Cell>
						{role.name}
					</Table.Cell>
					<Table.Cell>
						<em>
							{role.isPrincipalInvestigatorRole ? dictionary.principalInvestigatorRoleNote : null}
						</em>
					</Table.Cell>
					<Table.Cell>
						<Toggle
							label="Active research team role"
							checked={role.active}
							onChange={() => {
								editRoleById(id, { active: !role.active });
							}}
							disabled={role.isPrincipalInvestigatorRole || !isEditMode || actionsDisabled}
						/>
					</Table.Cell>
				</>
			)}
		</tr>
	);
};
